import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SparkModalPortal, Modal as SparkModal } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import UploadDataTab from '@/features/datasets/components/DataCollection/UploadDataTab';
import FromLibraryTab from '@/features/datasets/components/DataCollection/FromLibraryTab';
import HuggingFaceTab from '@/features/datasets/components/DataCollection/HuggingFaceTab';
import GenerateDataWithAIModal from '@/features/datasets/components/DataCollection/GenerateDataWithAI/GenerateDataWithAIModal';
import RetrievalAgentModal from '@/features/datasets/components/DataCollection/RetrievalAgent/RetrievalAgentModal';
import LibraryPickModal from '@/features/datasets/components/LibraryPickModal';
import { runAIGenerationDataset } from '@/features/datasets/utils/aiGenerationDataset';
import { runRetrievalAgent } from '@/features/datasets/utils/retrievalAgentDataset';
import DataCollectionPanel, {
    formFromUsecase,
    visibleTargetsFromUploadTarget,
} from '@/features/tasks/components/CreationWizard/dataCollection/DataCollectionPanel';
import '@/features/tasks/styles/task-drawer.css';

const TITLE_BY_SOURCE = {
    library: 'From Library',
    huggingface: 'Hugging Face',
    upload: 'Upload Data',
};

function uploadModalTitle(source, uploadTarget) {
    if (source === 'library') {
        if (uploadTarget === 'benchmark') return 'Upload Benchmark';
        if (uploadTarget === 'dataset') return 'Upload Dataset';
        return 'Upload Data';
    }
    if (source !== 'upload') return TITLE_BY_SOURCE[source] ?? 'Upload Data';
    if (uploadTarget === 'benchmark') return 'Upload Benchmark';
    if (uploadTarget === 'dataset') return 'Upload Dataset';
    return 'Upload Data';
}

/**
 * Shared "Add Data" modal used on both the Task page and the Datasets page.
 *
 * - Task context + upload / library: reuses the create-task Data Collection
 *   panel (scoped via `uploadTarget`). OS picks, drops, and library picks
 *   hide the Data Upload cards so the modal starts at Data Creation. Library
 *   opens a picker that autofills the new panel — never the legacy
 *   DatasetUploadStepsModal.
 * - Task context + Hugging Face: selection-only when `onHuggingFacePick` is
 *   set (create-task Data Collection); otherwise the existing in-modal mapping
 *   + upload used from an existing task's Add Data.
 * - Standalone (no usecase): legacy UploadDataTab / FromLibraryTab.
 */
const AddDataModal = ({
    open,
    onClose,
    source = 'upload',
    uploadTarget,
    initialDroppedFiles = null,
    onInitialDroppedFilesConsumed,
    usecase = null,
    subGroup = null,
    onSuccess,
    onHuggingFacePick,
}) => {
    const { refreshUsecases, seedGenerationProgress, seedRetrievalAgentProgress } = useContext(GlobalContext);
    const [isDataDetailsOpen, setIsDataDetailsOpen] = useState(false);
    const [isUploadDataAnalysisLoading, setIsUploadDataAnalysisLoading] = useState(false);
    const [datasetOptions, setDatasetOptions] = useState([]);
    const [benchmarkOptions, setBenchmarkOptions] = useState([]);
    const [libraryOptionsLoading, setLibraryOptionsLoading] = useState(false);
    const [currentCheckedDatasets, setCurrentCheckedDatasets] = useState([]);
    const [currentCheckedBenchmarks, setCurrentCheckedBenchmarks] = useState([]);
    const [isGenerateWithAiOpen, setIsGenerateWithAiOpen] = useState(false);
    const [isRetrievalAgentOpen, setIsRetrievalAgentOpen] = useState(false);
    const [isHuggingFaceOpen, setIsHuggingFaceOpen] = useState(false);
    const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
    const [libraryPickerTarget, setLibraryPickerTarget] = useState(undefined);
    const [pendingLibraryPick, setPendingLibraryPick] = useState(null);
    const [pendingHuggingFacePick, setPendingHuggingFacePick] = useState(null);
    // Latch: OS picker / drop / library pick seeds the panel, then the seed
    // prop is cleared. Keep hiding the Data Upload cards for this open.
    const [hideUploadSection, setHideUploadSection] = useState(false);
    // Entry overlay: Data Overview opened this modal only to show the picker.
    // Ref (not state) so a pick in the same click is not treated as dismiss —
    // setState would still read true when onClose runs after onPick.
    const libraryPickerIsEntryRef = useRef(false);

    const wizardForm = useMemo(() => formFromUsecase(usecase), [usecase]);
    const [panelForm, setPanelForm] = useState(wizardForm);
    useEffect(() => {
        setPanelForm(wizardForm);
    }, [wizardForm]);

    const useWizardDataCollection =
        (source === 'upload' || source === 'library') && Boolean(usecase && panelForm);

    useEffect(() => {
        setCurrentCheckedDatasets(usecase?.datasets || []);
        setCurrentCheckedBenchmarks(usecase?.benchmarks || []);
    }, [usecase?._id, usecase?.datasets, usecase?.benchmarks]);

    useEffect(() => {
        if (!open && isDataDetailsOpen) {
            setIsDataDetailsOpen(false);
        }
    }, [open, isDataDetailsOpen]);

    useEffect(() => {
        if (source === 'huggingface' && isDataDetailsOpen) {
            setIsDataDetailsOpen(false);
        }
    }, [source, isDataDetailsOpen]);

    useEffect(() => {
        if (!open) {
            setIsGenerateWithAiOpen(false);
            setIsHuggingFaceOpen(false);
            setLibraryPickerOpen(false);
            setLibraryPickerTarget(undefined);
            setPendingLibraryPick(null);
            setPendingHuggingFacePick(null);
            setHideUploadSection(false);
            libraryPickerIsEntryRef.current = false;
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (Array.isArray(initialDroppedFiles) && initialDroppedFiles.length > 0) {
            setHideUploadSection(true);
        }
    }, [open, initialDroppedFiles]);

    useEffect(() => {
        if (!open) return;
        if (source === 'generateAi' && usecase) {
            setIsGenerateWithAiOpen(true);
        }
    }, [open, source, usecase]);

    // From Library entry: open Data Collection and immediately show the picker.
    // Hide the Data Upload cards from the start (like the dropped-files path)
    // so the panel never hydrates already-attached datasets into its slots —
    // otherwise the shared-columns check can silently drop the library pick
    // when its columns don't overlap the attached datasets' columns.
    useEffect(() => {
        if (!open || !useWizardDataCollection || source !== 'library') return;
        setLibraryPickerTarget(uploadTarget);
        setLibraryPickerOpen(true);
        setHideUploadSection(true);
        libraryPickerIsEntryRef.current = true;
    }, [open, useWizardDataCollection, source, uploadTarget]);

    const refreshLibraryOptions = useCallback(
        (onDone) => {
            const clientId = usecase?.client_id;
            if (!clientId) {
                onDone?.();
                return;
            }
            setLibraryOptionsLoading(true);
            data_from_server_post(
                'datasets-and-benchmarks',
                { value: clientId },
                (result) => {
                    const dedupeByValue = (options) => {
                        const seen = new Set();
                        return options.filter((option) => {
                            if (seen.has(option.value)) return false;
                            seen.add(option.value);
                            return true;
                        });
                    };
                    const formattedDatasets = (result?.datasets || []).map((item) => ({
                        value: item.value,
                        label: item.value,
                        task_type: item.task_type,
                        column_mappings: item.column_mappings,
                        extra_params_keys: item.extra_params_keys || [],
                        file_column_names: item.file_column_names || [],
                        data_creation_type: item.data_creation_type,
                    }));
                    const formattedBenchmarks = (result?.benchmarks || []).map((item) => ({
                        value: item.value,
                        label: item.value,
                        task_type: item.task_type,
                        column_mappings: item.column_mappings,
                        extra_params_keys: item.extra_params_keys || [],
                        file_column_names: item.file_column_names || [],
                        data_creation_type: item.data_creation_type,
                    }));
                    setDatasetOptions(dedupeByValue(formattedDatasets));
                    setBenchmarkOptions(dedupeByValue(formattedBenchmarks));
                    setLibraryOptionsLoading(false);
                    onDone?.();
                },
                () => {
                    setLibraryOptionsLoading(false);
                    onDone?.();
                }
            );
        },
        [usecase?.client_id]
    );

    useEffect(() => {
        if (!open) return;
        if (source === 'library' || useWizardDataCollection) {
            refreshLibraryOptions();
        }
    }, [open, source, useWizardDataCollection, refreshLibraryOptions]);

    const handleSuccess = useCallback(() => {
        onSuccess?.();
        onClose?.();
    }, [onSuccess, onClose]);

    const visibleTargets = visibleTargetsFromUploadTarget(uploadTarget);

    const libraryPickerOptions =
        (libraryPickerTarget || uploadTarget) === 'benchmark'
            ? benchmarkOptions
            : (libraryPickerTarget || uploadTarget) === 'dataset'
              ? datasetOptions
              : [...datasetOptions, ...benchmarkOptions];

    const openLibraryPicker = (target = null) => {
        setLibraryPickerTarget(target || uploadTarget);
        setLibraryPickerOpen(true);
    };

    const handleLibraryPickerClose = () => {
        if (libraryPickerIsEntryRef.current) {
            libraryPickerIsEntryRef.current = false;
            onClose?.();
            return;
        }
        setLibraryPickerOpen(false);
    };

    const overlayOpen =
        isDataDetailsOpen ||
        libraryPickerOpen ||
        isGenerateWithAiOpen ||
        isHuggingFaceOpen ||
        (open && source === 'generateAi');

    return (
        <>
            <SparkModalPortal
                open={open}
                onClose={onClose}
                mask={!overlayOpen}
                keyboard={!overlayOpen}
                hidden={overlayOpen}
                destroyOnClose
                size={useWizardDataCollection || source === 'upload' ? 'wide' : 'text'}
                className="task-upload-data-modal"
            >
                <SparkModal
                    title={uploadModalTitle(source, uploadTarget)}
                    showSubtitle={false}
                    showScrollBar
                    fixedHeight={false}
                    showFooter={false}
                    showOverlay={false}
                    showClose={!isDataDetailsOpen}
                    onCloseClick={onClose}
                >
                    {useWizardDataCollection ? (
                        <DataCollectionPanel
                            variant="modal"
                            usecase={usecase}
                            form={panelForm}
                            onFormChange={setPanelForm}
                            onComplete={handleSuccess}
                            visibleTargets={visibleTargets}
                            initialDroppedFiles={initialDroppedFiles}
                            onInitialDroppedFilesConsumed={onInitialDroppedFilesConsumed}
                            hideUploadSection={hideUploadSection}
                            onDismiss={onClose}
                            pendingLibraryPick={pendingLibraryPick}
                            onPendingLibraryPickConsumed={() => setPendingLibraryPick(null)}
                            pendingHuggingFacePick={pendingHuggingFacePick}
                            onPendingHuggingFacePickConsumed={() => setPendingHuggingFacePick(null)}
                            onOpenLibrary={openLibraryPicker}
                            onOpenHuggingFace={() => setIsHuggingFaceOpen(true)}
                            onOpenGenerateAi={() => setIsGenerateWithAiOpen(true)}
                            onOpenRetrievalAgent={() => setIsRetrievalAgentOpen(true)}
                        />
                    ) : source === 'library' ? (
                        <FromLibraryTab
                            visibleTarget={uploadTarget}
                            datasetOptions={datasetOptions}
                            benchmarkOptions={benchmarkOptions}
                            currentCheckedDatasets={currentCheckedDatasets}
                            setCurrentCheckedDatasets={setCurrentCheckedDatasets}
                            currentCheckedBenchmarks={currentCheckedBenchmarks}
                            setCurrentCheckedBenchmarks={setCurrentCheckedBenchmarks}
                            currentUsecase={usecase}
                            isExternalFileLoading={false}
                            refreshLibraryOptions={refreshLibraryOptions}
                            subGroup={subGroup}
                            onDataDetailsVisibilityChange={setIsDataDetailsOpen}
                            onComplete={handleSuccess}
                        />
                    ) : source === 'huggingface' ? (
                        <HuggingFaceTab
                            currentUsecase={usecase}
                            selectedTaskType={usecase?.task_type}
                            onPick={onHuggingFacePick}
                            handleUploadDatasetSuccess={onHuggingFacePick ? undefined : handleSuccess}
                            handleUploadBenchmarkSuccess={onHuggingFacePick ? undefined : handleSuccess}
                            onUploadSuccess={onHuggingFacePick ? undefined : handleSuccess}
                            setIsDataAnalysisLoading={
                                onHuggingFacePick ? undefined : setIsUploadDataAnalysisLoading
                            }
                        />
                    ) : (
                        <UploadDataTab
                            currentUsecase={usecase}
                            selectedTaskType={usecase?.task_type}
                            uploadTarget={uploadTarget}
                            initialDroppedFiles={initialDroppedFiles}
                            onInitialDroppedFilesConsumed={onInitialDroppedFilesConsumed}
                            handleUploadDatasetSuccess={handleSuccess}
                            handleUploadBenchmarkSuccess={handleSuccess}
                            handleUploadExternalSuccess={handleSuccess}
                            onUploadSuccess={handleSuccess}
                            setIsDataAnalysisLoading={setIsUploadDataAnalysisLoading}
                            refreshLibraryOptions={handleSuccess}
                            onDataDetailsVisibilityChange={setIsDataDetailsOpen}
                            onUploadFlowDone={onClose}
                        />
                    )}
                </SparkModal>
            </SparkModalPortal>

            {useWizardDataCollection && (
                <LibraryPickModal
                    open={libraryPickerOpen}
                    onClose={handleLibraryPickerClose}
                    loading={libraryOptionsLoading}
                    options={libraryPickerOptions}
                    kind={
                        (libraryPickerTarget || uploadTarget) === 'benchmark'
                            ? 'benchmark'
                            : 'dataset'
                    }
                    onPick={(selectedOptions) => {
                        const defaultTarget = libraryPickerTarget || uploadTarget;
                        libraryPickerIsEntryRef.current = false;
                        setHideUploadSection(true);
                        setPendingLibraryPick({
                            picks: (selectedOptions || []).map((option) => ({
                                target:
                                    defaultTarget ||
                                    (benchmarkOptions.some((o) => o.value === option.value)
                                        ? 'benchmark'
                                        : 'dataset'),
                                option,
                            })),
                        });
                        setLibraryPickerOpen(false);
                    }}
                />
            )}

            {useWizardDataCollection && (
                <AddDataModal
                    open={isHuggingFaceOpen}
                    onClose={() => setIsHuggingFaceOpen(false)}
                    source="huggingface"
                    uploadTarget={uploadTarget}
                    usecase={usecase}
                    subGroup={subGroup}
                    onHuggingFacePick={(pick) => {
                        setPendingHuggingFacePick(pick);
                        setIsHuggingFaceOpen(false);
                    }}
                />
            )}

            {(useWizardDataCollection || source === 'generateAi') && (
                <GenerateDataWithAIModal
                    visible={isGenerateWithAiOpen}
                    onCancel={() => {
                        setIsGenerateWithAiOpen(false);
                        if (source === 'generateAi') onClose?.();
                    }}
                    usecase={usecase}
                    onGenerate={(payload) =>
                        runAIGenerationDataset({ usecase, payload, seedGenerationProgress }).then((res) => {
                            setIsGenerateWithAiOpen(false);
                            refreshUsecases?.();
                            if (source === 'generateAi') onClose?.();
                            return res;
                        })
                    }
                />
            )}

            {useWizardDataCollection && (
                <RetrievalAgentModal
                    visible={isRetrievalAgentOpen}
                    onCancel={() => setIsRetrievalAgentOpen(false)}
                    usecase={usecase}
                    onInitiate={(payload) =>
                        runRetrievalAgent({ usecase, payload, seedRetrievalAgentProgress }).then((res) => {
                            setIsRetrievalAgentOpen(false);
                            refreshUsecases?.();
                            return res;
                        })
                    }
                />
            )}
        </>
    );
};

export default AddDataModal;
